import request from '@/http';
import { 
  ApiResponse, 
  UserProfile, 
  AvatarUploadResponse, 
  RegisterRequest, 
  LoginRequest, 
  LoginResponse,
  InvitationCode,
  InvitationCodeIdentityRequest,
  InvitationImportSummary,
  Team,
  TeamMember,
  CreateTeamRequest,
  UpdateTeamRequest,
  JoinTeamRequest,
  Group,
  GroupMember,
  CreateGroupRequest,
  UpdateGroupRequest,
  AddGroupMemberRequest,
  GroupMemberChangeResult,
  // 待办事项类型（主页日历模块），见 types/account.ts
  TodoItem
} from '../types/account';
import { PaginatedData } from '../http/types';
import { useAuthStore } from '../store/useAuthStore';

enum Url {
  login = '/api/account/api-token-auth/',
  register = '/api/account/register/',
  uploadAvatar = '/api/account/avatar/',
  profile = '/api/account/profile/',
  invitationCode = '/api/account/invitation_code/',
  teams = '/api/account/teams/',
  groups = '/api/account/groups/',
}


export const accountService = {
  // 用户注册
  async register(data: RegisterRequest): Promise<ApiResponse<RegisterRequest>> {
    const response = await request.post(Url.register, data);
    return response.data;
  },

   // 用户登录
  async login(data: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    const response = await request.post<LoginResponse>(Url.login, data);
    if (response.data.data.token && response.data.data.user) {
      useAuthStore.getState().setAuth(response.data.data.token, response.data.data.user);
    }
    return response.data;
  },

  // 上传头像
  async uploadAvatar(formData: FormData): Promise<ApiResponse<AvatarUploadResponse>> {
    const response = await request.post(Url.uploadAvatar, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // 获取用户资料
  async getProfile(): Promise<ApiResponse<UserProfile>> {
    const response = await request.get(Url.profile);
    return response.data;
  },

  // 创建用户资料（首次登录时使用）
  async createProfile(data: Partial<UserProfile>): Promise<ApiResponse<UserProfile>> {
    const response = await request.post(Url.profile, data);
    return response.data;
  },

  // 更新用户资料
  async updateProfile(data: UserProfile): Promise<ApiResponse<UserProfile>> {
    const response = await request.put(Url.profile, data);
    return response.data;
  },

  // 获取头像完整URL
  getAvatarUrl(avatar: string): string {
    return request.getAvatarUrl(avatar);
  },

  // 获取当前用户的头像URL
  async getCurrentUserAvatar(): Promise<string> {
    try {
      const response = await this.getProfile();
      if (response.data && response.data.avatar) {
        console.log(response.data.avatar);
        return this.getAvatarUrl(response.data.avatar);
      }
      return '';
    } catch (error) {
      console.error('获取用户头像失败:', error);
      return '';
    }
  },

  // 登出
  logout() {
    useAuthStore.getState().clearAuth();
  },

  // 检查是否已登录
  isAuthenticated(): boolean {
    return !!useAuthStore.getState().token;
  },

  // ================== 邀请码管理相关API（实名制：一人一码） ==================

  // 获取邀请码列表（可按使用状态/关键字筛选）
  async getInvitationCodes(
    page?: number,
    filters?: { is_used?: boolean; keyword?: string },
  ): Promise<ApiResponse<PaginatedData<InvitationCode>>> {
    const params: Record<string, unknown> = {};
    if (page) params.page = page;
    if (filters?.is_used !== undefined) params.is_used = filters.is_used;
    if (filters?.keyword) params.keyword = filters.keyword;
    const response = await request.get(Url.invitationCode, { params });
    return response.data;
  },

  // 新增单个实名邀请码
  async createInvitationCode(
    data: InvitationCodeIdentityRequest,
  ): Promise<ApiResponse<InvitationCode>> {
    const response = await request.post(Url.invitationCode, data);
    return response.data;
  },

  // 修改未使用邀请码的姓名/学校/用户组
  async updateInvitationCode(
    codeId: number,
    data: InvitationCodeIdentityRequest,
  ): Promise<ApiResponse<InvitationCode>> {
    const response = await request.put(`${Url.invitationCode}${codeId}/`, data);
    return response.data;
  },

  // 删除邀请码
  async deleteInvitationCode(codeId: number): Promise<ApiResponse<null>> {
    const response = await request.delete(`${Url.invitationCode}${codeId}/`);
    return response.data;
  },

  /**
   * Excel 名单批量导入：成功时后端直接回传结果 Excel（blob）。
   * 返回 blob + 响应头统计（X-Created / X-Reused / X-Reissued / X-Skipped）；
   * **跨域环境下这些自定义头可能读不到**（需后端 Access-Control-Expose-Headers），
   * 此时 summary 为 null，调用方只提示「已下载」而不要谎报 0。
   * 校验失败时后端返回 400 JSON（由调用方 catch 后解析 data.errors）。
   */
  async importInvitationCodes(
    file: File,
    groupId?: number | null,
  ): Promise<{ blob: Blob; summary: InvitationImportSummary | null }> {
    const form = new FormData();
    form.append('file', file);
    if (groupId) form.append('group_id', String(groupId));
    const response = await request.post(`${Url.invitationCode}import/`, form, {
      responseType: 'blob',
      // 🔴 必须显式声明 multipart：axios 实例默认 Content-Type 是 application/json，
      // 而 axios v1 的 transformRequest 遇到「FormData + application/json」会把
      // FormData 转成 JSON 字符串发送，后端 c.FormFile 拿不到文件（报「请选择要导入的 Excel 文件」）。
      // 显式写 multipart 后浏览器会自动补 boundary（对齐其它上传接口的写法）。
      headers: { 'Content-Type': 'multipart/form-data' },
      // 导入错误由本页解析 blob 后自行提示（含逐行错误），跳过全局拦截器提示
      silent: true,
    });

    const headers = response.headers ?? {};
    const pick = (name: string): number | undefined => {
      const raw = (headers as Record<string, unknown>)[name];
      if (raw === undefined || raw === null || raw === '') return undefined;
      const n = Number(raw);
      return Number.isFinite(n) ? n : undefined;
    };
    const created = pick('x-created');
    const reused = pick('x-reused');
    const reissued = pick('x-reissued');
    const skipped = pick('x-skipped');
    const missingAll =
      created === undefined && reused === undefined && reissued === undefined && skipped === undefined;

    return {
      blob: response.data as unknown as Blob,
      summary: missingAll
        ? null
        : {
            created: created ?? 0,
            reused: reused ?? 0,
            reissued: reissued ?? 0,
            skipped: skipped ?? 0,
          },
    };
  },

  // 下载导入模板
  async downloadInvitationTemplate(): Promise<Blob> {
    const response = await request.get(`${Url.invitationCode}import/template/`, {
      responseType: 'blob',
    });
    return response.data as any;
  },

  // 导出邀请码到Excel（isUsed=false 时只导「尚未注册」名单）
  async exportInvitationCodes(isUsed?: boolean): Promise<Blob> {
    const params = isUsed === undefined ? {} : { is_used: isUsed };
    const response = await request.get(`${Url.invitationCode}export/`, {
      params,
      responseType: 'blob',
    });
    return response.data as any;
  },

  // ================== 团队管理相关API ==================

  // 获取团队列表
  async getTeams(): Promise<ApiResponse<PaginatedData<Team>>> {
    const response = await request.get(Url.teams);
    return response.data;
  },

  // 获取团队详情
  async getTeam(teamId: number): Promise<ApiResponse<Team>> {
    const response = await request.get(`${Url.teams}${teamId}/`);
    return response.data;
  },

  // 创建团队
  async createTeam(data: CreateTeamRequest): Promise<ApiResponse<Team>> {
    const response = await request.post(Url.teams, data);
    return response.data;
  },

  // 更新团队信息
  async updateTeam(teamId: number, data: UpdateTeamRequest): Promise<ApiResponse<Team>> {
    const response = await request.put(`${Url.teams}${teamId}/`, data);
    return response.data;
  },

  // 删除团队
  async deleteTeam(teamId: number): Promise<ApiResponse<null>> {
    const response = await request.delete(`${Url.teams}${teamId}/`);
    return response.data;
  },

  // 加入团队
  async joinTeam(teamId: number, data: JoinTeamRequest): Promise<ApiResponse<null>> {
    const requestData = {
      ...data,
      team_id: teamId,
    };
    const response = await request.post(`${Url.teams}${teamId}/join/`, requestData);
    return response.data;
  },

  // 获取团队成员列表
  async getTeamMembers(teamId: number): Promise<ApiResponse<TeamMember[]>> {
    const response = await request.get(`${Url.teams}${teamId}/members/`);
    return response.data;
  },

  // 获取用户所属的团队
  async getMyTeam(): Promise<ApiResponse<Team[]>> {
    const response = await request.get(`${Url.teams}my_team/`);
    return response.data;
  },

  // 退出团队
  async leaveTeam(teamId: number): Promise<ApiResponse<null>> {
    const response = await request.post(`${Url.teams}${teamId}/leave/`);
    return response.data;
  },

  // ================== 用户组管理相关API ==================

  // 获取用户组列表（limit 用于下拉一次性取回，后端上限 100）
  async getGroups(keyword?: string, page?: number, limit?: number): Promise<ApiResponse<PaginatedData<Group>>> {
    const params: any = {};
    if (keyword) params.keyword = keyword;
    if (page) params.page = page;
    if (limit) params.limit = limit;
    const response = await request.get(Url.groups, { params });
    return response.data;
  },

  // 获取用户组详情
  async getGroup(groupId: number): Promise<ApiResponse<Group>> {
    const response = await request.get(`${Url.groups}${groupId}/`);
    return response.data;
  },

  // 创建用户组
  async createGroup(data: CreateGroupRequest): Promise<ApiResponse<Group>> {
    const response = await request.post(Url.groups, data);
    return response.data;
  },

  // 更新用户组信息
  async updateGroup(groupId: number, data: UpdateGroupRequest): Promise<ApiResponse<Group>> {
    const response = await request.patch(`${Url.groups}${groupId}/`, data);
    return response.data;
  },

  // 删除用户组
  async deleteGroup(groupId: number): Promise<ApiResponse<null>> {
    const response = await request.delete(`${Url.groups}${groupId}/`);
    return response.data;
  },

  // 添加用户到组（传 user_ids 即批量；批量返回 {added, skipped}）
  async addGroupMember(groupId: number, data: AddGroupMemberRequest): Promise<ApiResponse<GroupMemberChangeResult>> {
    const response = await request.post(`${Url.groups}${groupId}/add_member/`, data);
    return response.data;
  },

  // 从组中移除用户（传 user_ids 即批量；批量返回 {removed}）
  async removeGroupMember(groupId: number, data: AddGroupMemberRequest): Promise<ApiResponse<GroupMemberChangeResult>> {
    const response = await request.post(`${Url.groups}${groupId}/remove_member/`, data);
    return response.data;
  },

  // 获取组成员列表
  async getGroupMembers(groupId: number): Promise<ApiResponse<GroupMember[]>> {
    const response = await request.get(`${Url.groups}${groupId}/members/`);
    return response.data;
  },

  // 获取用户所属的组
  async getUserGroups(userId: number): Promise<ApiResponse<Group[]>> {
    const response = await request.get(`${Url.groups}user_groups/`, {
      params: { user_id: userId }
    });
    return response.data;
  },

  // ===== 每日打卡（错误提示由组件统一处理，不走全局红×） =====
  async checkIn(): Promise<ApiResponse<{
    already_checked_in: boolean;
    streak: number;
    reward: number;
    points: number;
    leveled_up: boolean;
    level: number;
    next_level_experience: number;
  }>> {
    const response = await request.post('/api/account/check_in/', undefined, { silent: true } as any);
    return response.data;
  },

  // ===== 待办事项（主页日历模块，挂在 /api/account 前缀下） =====
  async getTodos(): Promise<ApiResponse<TodoItem[]>> {
    const response = await request.get('/api/account/todo/');
    return response.data;
  },
  async createTodo(data: { title: string; date: string; scope?: 'global' | 'personal' }): Promise<ApiResponse<TodoItem>> {
    const response = await request.post('/api/account/todo/', data);
    return response.data;
  },
  async updateTodo(id: number, data: { title?: string; date?: string; done?: boolean }): Promise<ApiResponse<TodoItem>> {
    const response = await request.put(`/api/account/todo/${id}/`, data);
    return response.data;
  },
  async deleteTodo(id: number): Promise<ApiResponse<{ id: number }>> {
    const response = await request.delete(`/api/account/todo/${id}/`);
    return response.data;
  },
}; 